import type { TrafficType } from '@pt/enums/insights'
import type { ListDataPagingParams } from 'request'
import type { EnumToUnion } from 'types'
import type { UsageType, BillStatus } from '@pt/enums/user'
import { RechargeType, BillingType, BillType } from '@/common/permission'
import { CountryListResponseData } from '../common/index.type'
import { PartnerApproveStatus, PartnerCategory } from '@pt/enums/partner'

export enum AccountStatus {
  ACTIVE = 'active', // 已授权
  PENDING = 'pending', // 授权中
  UNBOUND = 'unactive', // 未授权
  DISABLED = 'disabled', // 授权失败
}

export enum ReportPermissionStatus {
  ALLOW = 'allow', // 允许
  NORANGE = 'no-range', // 未授权数据范围
  NOADV = 'no-adv', // 无广告主
  NODATA = 'no-data', // 无授权数据
}

// 授权平台类型
export enum AdAuthType {
  Channel = 'channel', // 授权渠道
  Platform = 'platform', // 授权平台
}

//  校验用户注册信息
export enum ExistsStatus {
  AlAccount = 1, // 已有账号,直接使用pt平台
  NeedRegister, // 需要注册到ss
  DirectRegister, // 在pt平台直接注册
}

export enum GrantType {
  Owner = 'owner', // 所属账号
  SubAccount = 'sub_account', // 子账号
  Assistant = 'assistant', // 协助账号
}

type ShowAll = 1 | 2 // 是否显示所有报表数据，1:是 2:否
type AdAccountAuthStatus = EnumToUnion<typeof AccountStatus>
export type ReportPermission = 0 | 1 | 2 | 3 // 数据范围权限 0: 未设置 1:全部 2:仅Playturbo 3:不拉取
export type ExistsPermission = 1 | 2 | 3 // 校验用户注册信息 1: 已有账号 2:需要注册到ss  3:可以直接注册

export interface AdAccount {
  id: number
  pid: number
  adv_id: number
  traffic_type: TrafficType
  account_name: string
  email: string
  show_all: ShowAll
  status: AdAccountAuthStatus
  xmp_status?: AdAccountAuthStatus
  report_permission: ReportPermission
  created_time: string
  sub_accounts?: AdAccount[]
  pushed?: boolean
  type: string
  platform_name: string
  xmp_user_id?: number
  grant_type: GrantType
}

export interface AdSubAccount {
  id: number
  account_id: number
  pid: number
  adv_id: number
  traffic_type: TrafficType
  account_name: string
  email: string
  show_all: ShowAll
  status: AdAccountAuthStatus
  xmp_status: AdAccountAuthStatus
  created_time: string
}

export interface GetAdAccountListQuery extends ListDataPagingParams {
  search_field?: string
  keyword?: string
  account_name?: string
  email?: string
  end_time?: string
  export?: number // 1:导出,其他不可导出
  order?: string // 排序字段
  type?: string
  statuses?: number[]
  traffic_type?: number | string
  traffic_types?: number | string
  platform_name?: string
}

export interface GetAdAccountSearchQuery {
  traffic_types?: number[]
}

export type AdAccountLite = Pick<
  AdAccount,
  | 'account_name'
  | 'id'
  | 'pid'
  | 'sub_accounts'
  | 'traffic_type'
  | 'xmp_user_id'
>

export interface CampaignSet {
  app_id: string
  app_name: string
  package_name: string
  platform: string
}

export interface CreativeSet {
  id: string
  name: string
}

export interface UserFolder {
  folder_id: number
  folder_name: string
}

export interface AdAccountAuthQuery {
  traffic_type: TrafficType | '' // 渠道
  account_name?: string // 账号  Applovin、IronSource必填
  email?: string // 邮箱
  api_key?: string // API Key
  report_key?: string // Report Key  Applovin必填
  access_key?: string // Access Key MTG必填
  secret_key?: string // Secret Key unity、IronSource必填
  client_id?: string // Client id Applovin必填
  key_id?: string // Key ID unity必填
  org_id?: string // Org ID unity必填
  org_core_id?: string // Org Core ID unity必填
  show_all?: ShowAll // 是否显示所有报表数据
  report_permission: ReportPermission
  is_agree?: boolean // 是否同意授权
  client_secret?: string // Client secret Applovin必填
  refresh_token?: string // Refresh token IronSource必填
  type: AdAuthType // 平台类型
  platform_name?: string // 授权平台:type为platform时必填
}

export interface registerAccountQuery {
  email: string
  validate_code: string
  username: string
  cell?: string
  country?: string
  organization?: string
  representative?: string
  password: string
  contact: string
  is_agree: boolean
  repassword: string
  referer?: string // 访问来源
}

export interface AdAccountBindQuery {
  id?: number // 主帐号记录id或者子帐号account_id，当子帐号account_id不为0
  pid?: number // 子帐号account_id=0时，主帐号记录id
  sub_adv_id?: number // 子帐号account_id=0时，子帐号adv_id
}

export interface AdAuthInfo {
  access_key: string
  api_key: string
  email: string
}

export interface PointSetting {
  report: boolean
  traffic_type: number
}

export interface AdReportPermissionQuery {
  accounts: {
    id: number
    report_permission: ReportPermission
  }[]
}

export interface ProfileInfo {
  name: string
  email: string
  cc_email: string[]
  id?: number
  contact: string
  organization: string
  currency: number
  cell?: string
  country?: string
  category?: string
}

export interface GetBillingUsageQuery {
  end_time?: string
  start_time?: string
  page: number
  page_size: number
  usage_types: string
  order: string
}

export interface BillingUsage {
  date: string
  usage_type: UsageType
  value: number
  biz_type: string
}

export interface GetBillDetailQuery {
  end_date: string
  start_date: string
  page: number
  page_size: number
  order: string
  export?: 1
}

export interface BillDetail {
  /** 欠费金额 */
  arrears_amount: number
  /** 业务类型 **/
  biz_type: BillingType
  /** 计费数量 **/
  quantity: number
  /** 业务ID **/
  object_id: number
  /** 账单金额 */
  total_amount: number
  /** 计费展示 */
  bill_impression: number
  cpm: number
  date: string
  /** 当天总展示 */
  impression: number
  /** 已支付金额 */
  paid_amount: number
  /** 计费方式 */
  charge_mode: RechargeType
  /** rev套餐计费展示 */
  cost: number
  /** 单价 */
  unit_price: number
}

export interface GetBillsQuery {
  end_time?: string
  start_time?: string
  page: number
  page_size: number
  order: string
  status: string
}

export interface Bills {
  arrears_amount: number
  total_amount: number
  date: string
  end_date: string
  paid_amount: number
  start_date: string
  status: BillStatus
}

export interface GetBillingMaterialsQuery {
  page: number
  page_size: number
  order: string
  keyword?: string
  project_ids?: string
  search_field?: string
  status?: string
}

export interface BillingMaterial {
  cpm: number
  created_time: string
  fingerprint: string
  project_id: number
  project_name: string
  total_bill_impression: number
  total_impression: number
  // 素材总费用
  total_service_fee: number
  traffic_type: number
  consume_type: RechargeType
  unit_price: number
  total_cost: number
  // 状态：1：计费中（默认） 2：已到达价格上限  3：已买断
  status: number
  // 	素材消耗费用
  service_fee: number
  // 停止计费日期
  stop_time: number
  // 停止计费方式
  stop_bill_method: string
  // 计费数量
  quantity: number
  // 买断费用
  creative_buyout_price: number
  // 价格上限
  creative_max_price: number
  // 项目来源
  source: string
  // 素材费用消耗，数组字段与上层级一致
  materials: BillingMaterial[]
  expanded: boolean
  channel_buyout_price: number
  channel_max_price: number
  mws_creative_name: string
  basic_id: number
  preview_url: string
  total_amount: number
}
export interface AdAccountChannel {
  // 该渠道下全部账号已设置报表权限
  all_set_report_permission: boolean

  // 是否有授权账号
  has_auth_account: boolean

  // 有推送&下载记录返回true、否则返回false；mtg、unity账号写死true
  has_download_records: boolean

  // 只要有一个授权账号拉取，则为true; 全部都不拉取，则为false
  pull_report_data: boolean

  traffic_type: TrafficType
}

export interface StatementQuery {
  order: string
  basic_ids?: number[]
  page?: number
  page_size?: number
  traffic_types: number[]
  time?: string[]
  start_date?: number
  end_date?: number
  export?: number
}

export interface StatementItem {
  bill_impression: number
  click: number
  cost: number
  date: number
  cpm: number
  fingerprint: string
  impression: number
  install: number
  service_fee: number
  traffic_type: number
  consume_type: RechargeType
  unit_price: number
  bill_type: BillType
  amount: number
  type: string
}

export interface ApplyTrailQuery {
  full_name?: string
  region?: string
  company?: string
  company_type?: string
  job_title?: string
  phone?: string
  contact_type?: string
  contact_account?: string
  m_username?: string
  interested_in?: string
  message?: string
  product_type?: string
  app_url?: string
  team_size?: string
  interesting_gameplay?: string[]
  isMtg?: boolean | ''
  interesting_videos?: string[]
  email?: string
  referer?: string
  lpUrl?: string
  adgroupid?: string
  campaignid?: string
  keyword?: string
}

export interface ApplyTrailResponse {
  days: number
  expired_date: string
  adv_id: number
}

/**
 * 合作方信息
 */
export interface UserPartnerInfo {
  partner_code: string
  /** 客户类别 */
  category: PartnerCategory
  name: string
  country_code: CountryListResponseData['code']
  address: string
  approve_status: PartnerApproveStatus
}

export interface UserPartnerResponse {
  list: UserPartnerInfo[]
  // 以下两个字段是当前用户的信息
  category: PartnerCategory
  approve_status: PartnerApproveStatus
}

/**
 * 协助账号信息
 */
export interface UserAssistantsInfo {
  assistant_id: number
  // 账号名称
  assistant_name: string
  id: number
}
